import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { displayReqNumber } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LOCATIONS = ["Houston HQ", "San Ramon", "New York", "Permian Basin", "Gulf of Mexico Platform", "Bakersfield"];
const PRIORITIES = ["Standard", "High", "Critical"];

export default function OrderDialog({ device, onClose, onSuccess }) {
  const { persona, userName, userEmail } = usePersona();
  const [color, setColor] = useState(null);
  const [accessories, setAccessories] = useState([]);
  const [justification, setJustification] = useState("");
  const [location, setLocation] = useState(LOCATIONS[0]);
  const [priority, setPriority] = useState("Standard");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (device) {
      setColor(device.color_options?.[0] || null);
      setAccessories([]);
      setJustification("");
      setLocation(LOCATIONS[0]);
      setPriority("Standard");
      setConfirmation(null);
    }
  }, [device]);

  if (!device) return null;

  const toggleAccessory = (a) => {
    setAccessories((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  };

  const submit = async () => {
    if (!justification.trim()) {
      toast.error("Please add a short business justification");
      return;
    }
    setSubmitting(true);
    try {
      const res = await endpoints.createOrder({
        device_id: device.id,
        persona_id: persona.id,
        user_name: userName || "Employee",
        user_email: userEmail || null,
        color,
        accessories,
        justification,
        delivery_location: location,
        priority,
      });
      setConfirmation(res);
      toast.success(`Ticket ${displayReqNumber(res)} created in ServiceNow`);
    } catch (e) {
      toast.error("Could not submit the order. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyReq = () => {
    const num = displayReqNumber(confirmation);
    if (num) {
      navigator.clipboard.writeText(num);
      toast.success("REQ number copied");
    }
  };

  const close = () => {
    if (confirmation) onSuccess?.(); else onClose?.();
  };

  return (
    <Dialog open={!!device} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="order-dialog" className="max-w-2xl bg-white p-0 gap-0 overflow-hidden">
        {!confirmation ? (
          <>
            <DialogHeader className="p-6 pb-4 border-b border-slate-200">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Order Device</div>
              <DialogTitle className="font-display text-2xl text-slate-900 mt-1">{device.name}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">{device.brand} · {device.category} · Est. ${device.price.toLocaleString()}</DialogDescription>
              {device.pre_approved === true && (
                <div className="mt-3 flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800" data-testid="approval-preapproved-notice">
                  <span className="mt-0.5">✓</span>
                  <span>This device is <strong>pre-approved for the {persona.name} persona</strong> — your order will fulfill without a manager sign-off.</span>
                </div>
              )}
              {device.pre_approved === false && (
                <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800" data-testid="approval-required-notice">
                  <span className="mt-0.5">⚑</span>
                  <span>This device is <strong>outside your {persona.name} persona&apos;s pre-approved set</strong>. A manager approval email will be sent before fulfillment.</span>
                </div>
              )}
            </DialogHeader>

            <div className="p-6 grid md:grid-cols-[180px_1fr] gap-6 max-h-[65vh] overflow-y-auto">
              <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-100 h-fit">
                <img src={device.image_url} alt={device.name} className="w-full h-40 object-cover" />
              </div>

              <div className="space-y-5">
                {device.color_options?.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Color / Configuration</Label>
                    <RadioGroup value={color} onValueChange={setColor} className="mt-2 flex flex-wrap gap-2" data-testid="order-color-group">
                      {device.color_options.map((c) => (
                        <label key={c} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md cursor-pointer text-sm ${color === c ? "border-[#0033A0] bg-[#0033A0]/5" : "border-slate-200"}`}>
                          <RadioGroupItem value={c} id={`color-${c}`} data-testid={`color-${c}`} />
                          {c}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {device.accessories?.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Add Accessories</Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {device.accessories.map((a) => (
                        <label key={a} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md cursor-pointer text-sm hover:bg-slate-50">
                          <Checkbox
                            checked={accessories.includes(a)}
                            onCheckedChange={() => toggleAccessory(a)}
                            data-testid={`accessory-${a.replace(/\s+/g, '-')}`}
                          />
                          {a}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Delivery Location</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger data-testid="delivery-location" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOCATIONS.map((l) => <SelectItem key={l} value={l} data-testid={`loc-${l.replace(/\s+/g, '-')}`}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Priority</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger data-testid="order-priority" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p} data-testid={`prio-${p}`}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="justification" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Business Justification</Label>
                  <Textarea
                    id="justification"
                    data-testid="order-justification"
                    placeholder="Briefly explain why this device is required..."
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    className="mt-1 min-h-24"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="p-6 pt-4 border-t border-slate-200 bg-slate-50 flex sm:justify-between items-center">
              <div className="text-xs text-slate-500 hidden sm:block">A ServiceNow REQ will be generated on submit.</div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button variant="outline" onClick={onClose} data-testid="order-cancel" className="flex-1 sm:flex-none">Cancel</Button>
                <Button
                  data-testid="order-submit"
                  onClick={submit}
                  disabled={submitting}
                  className="bg-[#0033A0] hover:bg-[#002573] text-white flex-1 sm:flex-none"
                >
                  {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                  Submit Order
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <div className="p-8" data-testid="order-confirmation">
            <div className="w-14 h-14 rounded-full bg-green-100 grid place-items-center mx-auto">
              <CheckCircle2 className="text-green-600" size={30} />
            </div>
            <h3 className="mt-4 font-display font-black text-2xl text-slate-900 text-center">Order submitted</h3>
            <p className="mt-1 text-sm text-slate-500 text-center">
              {confirmation.requires_approval
                ? `Awaiting approval from ${confirmation.approver_name}. A ServiceNow ticket is open.`
                : "A ServiceNow ticket has been created for your request."}
            </p>

            <div className="mt-6 border border-slate-200 rounded-md p-5 bg-slate-50">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">ServiceNow REQ Number</div>
                  <div data-testid="confirmation-req" className="font-mono font-bold text-2xl text-[#0033A0] mt-1">{displayReqNumber(confirmation)}</div>
                  {confirmation.sn_number && confirmation.sn_number !== confirmation.req_number && (
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">Local Flexspace REQ: {confirmation.req_number}</div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={copyReq} data-testid="copy-req-btn">
                  <Copy size={14} className="mr-2" /> Copy
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Device</div>
                  <div className="text-slate-900">{confirmation.device_name}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Status</div>
                  <div className="text-slate-900">{confirmation.status}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Delivery</div>
                  <div className="text-slate-900">{confirmation.delivery_location}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Priority</div>
                  <div className="text-slate-900">{confirmation.priority}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={close} data-testid="confirmation-close">Close</Button>
              <Button
                data-testid="confirmation-view-tickets"
                onClick={() => { close(); window.location.href = "/requests?view=orders"; }}
                className="bg-[#0033A0] hover:bg-[#002573] text-white"
              >
                View Tickets
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
